// Type local pour les étapes (compatible react-joyride v2 — évite les conflits avec le `Step` de react-floater).
export type Step = {
  target: string;
  title?: string;
  content: string;
  placement?: "top" | "bottom" | "left" | "right" | "center" | "bottom-end" | "top-end" | "bottom-start" | "top-start" | "auto";
  disableBeacon?: boolean;
};
import type { UserRole } from "@/lib/navigation";

/**
 * Étapes du tour guidé, par rôle.
 * Les sélecteurs ciblent des `data-tour="..."` posés sur les éléments clés.
 * Tous les tours commencent sur /dashboard (ou /stock/grille-tampon pour barman/cuisinier).
 */

const isMobile = (): boolean =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

/** Adapte les étapes au mobile : sur petits écrans, on remplace les placements latéraux
 *  par "center" pour les sélecteurs cachés (sidebar, etc.) et on neutralise les cibles
 *  systématiquement absentes (ex. data-tour="sidebar"). */
function adaptForMobile(steps: Step[]): Step[] {
  if (!isMobile()) return steps;
  return steps
    .filter((s) => s.target !== '[data-tour="sidebar"]') // sidebar masquée mobile
    .map((s) => {
      // Toute étape ciblant un item de nav (data-tour="nav-...") est en drawer mobile :
      // on la convertit en étape centrée pour ne pas pointer dans le vide.
      if (s.target.startsWith('[data-tour="nav-')) {
        return { ...s, target: "body", placement: "center" as const };
      }
      // Les éléments de la topbar restent visibles, on les laisse.
      return s;
    });
}

const COMMON_FINAL: Step = {
  target: '[data-tour="user-menu"]',
  title: "Votre compte",
  content:
    "Retrouvez ici votre profil, l'aide en ligne et l'option pour relancer ce tour à tout moment.",
  placement: "bottom-end",
  disableBeacon: true,
};

const SIDEBAR_STEP: Step = {
  target: '[data-tour="sidebar"]',
  title: "Navigation principale",
  content:
    "Tous les modules de l'application sont organisés ici dans l'ordre d'une journée type : Stock → Achats → Pertes → Dépenses → Recettes → Rapports.",
  placement: "right",
  disableBeacon: true,
};

const NOTIFS_STEP: Step = {
  target: '[data-tour="notifications"]',
  title: "Notifications",
  content:
    "Vous serez alerté ici en temps réel : ruptures de stock, validations à effectuer, transferts en attente, etc.",
  placement: "bottom-end",
  disableBeacon: true,
};

const DASHBOARD_WELCOME: Step = {
  target: "body",
  title: "Bienvenue sur Mandjaro Ops 👋",
  content:
    "Découvrons ensemble en 1 minute les principales fonctionnalités de l'application adaptées à votre rôle.",
  placement: "center",
  disableBeacon: true,
};

const STEPS_GERANTE: Step[] = [
  DASHBOARD_WELCOME,
  SIDEBAR_STEP,
  {
    target: '[data-tour="dashboard-kpis"]',
    title: "Vos indicateurs clés",
    content:
      "Suivez d'un coup d'œil le CA du jour, les marges, les ruptures et les arrêtés de caisse en attente.",
    placement: "bottom",
  },
  {
    target: '[data-tour="dashboard-alerts"]',
    title: "Alertes prioritaires",
    content:
      "Les alertes critiques (ruptures, lots qui expirent, validations) apparaissent en haut du dashboard.",
    placement: "bottom",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

const STEPS_PDG: Step[] = [
  DASHBOARD_WELCOME,
  SIDEBAR_STEP,
  {
    target: '[data-tour="dashboard-kpis"]',
    title: "Vue exécutive",
    content:
      "Vos indicateurs financiers et opérationnels sont consolidés ici, mis à jour en temps réel.",
    placement: "bottom",
  },
  {
    target: '[data-tour="nav-RAPPORTS"]',
    title: "Analytics & Rapports",
    content:
      "Accédez aux analyses approfondies, marges, balance matière et exports brandés Mandjaro.",
    placement: "right",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

const STEPS_GESTIONNAIRE: Step[] = [
  DASHBOARD_WELCOME,
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-STOCK"]',
    title: "Gestion du stock",
    content:
      "Articles, assignations aux zones, transferts, mouvements et ajustements : tout part d'ici.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-ACHATS"]',
    title: "Achats fournisseurs",
    content:
      "Créez vos bons de commande, enregistrez les réceptions et suivez les avoirs dûs.",
    placement: "right",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

const STEPS_CONTROLEUR: Step[] = [
  DASHBOARD_WELCOME,
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-ACHATS"]',
    title: "Validation des bons de commande",
    content:
      "Votre rôle : contrôler et valider (ou rejeter) les bons de commande soumis avant envoi au fournisseur.",
    placement: "right",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

const STEPS_BARMAN: Step[] = [
  {
    target: "body",
    title: "Bienvenue 👋",
    content:
      "Votre interface est centrée sur la grille tampon, les transferts, les pertes et les dépenses caisse.",
    placement: "center",
    disableBeacon: true,
  },
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-STOCK"]',
    title: "Grille tampon & transferts",
    content:
      "Confirmez les assignations reçues et déclarez vos transferts entre zones depuis le module Stock.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-PERTES & CASSE"]',
    title: "Déclarer une perte",
    content:
      "Toute casse ou perte doit être déclarée immédiatement ici pour garder l'inventaire juste.",
    placement: "right",
  },
  COMMON_FINAL,
];

const STEPS_CUISINIER: Step[] = [
  {
    target: "body",
    title: "Bienvenue 👋",
    content:
      "Votre interface vous permet de gérer la grille tampon cuisine, les transferts et les pertes.",
    placement: "center",
    disableBeacon: true,
  },
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-STOCK"]',
    title: "Vos opérations stock",
    content:
      "Confirmez vos assignations, faites vos transferts entre zones et tenez la grille tampon à jour.",
    placement: "right",
  },
  {
    target: '[data-tour="nav-PERTES & CASSE"]',
    title: "Déclarer une perte",
    content:
      "Toute perte cuisine doit être déclarée ici pour assurer la traçabilité.",
    placement: "right",
  },
  COMMON_FINAL,
];

const STEPS_SERVEUR: Step[] = [
  {
    target: "body",
    title: "Bienvenue en salle 👋",
    content:
      "Votre interface est dédiée à la saisie des pourboires et à la gestion des commandes spéciales.",
    placement: "center",
    disableBeacon: true,
  },
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-POURBOIRES"]',
    title: "Pourboires",
    content:
      "Saisissez les pourboires reçus et consultez votre historique de répartitions. Le solde de la caisse commune s'affiche en temps réel.",
    placement: "right",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

const STEPS_CHEF_SALLE: Step[] = [
  {
    target: "body",
    title: "Bienvenue, Chef de salle 👋",
    content:
      "Votre interface coordonne le service en salle : pourboires et coordination des serveurs.",
    placement: "center",
    disableBeacon: true,
  },
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-POURBOIRES"]',
    title: "Caisse commune pourboires",
    content:
      "Saisissez les pourboires, suivez le solde de la caisse commune et l'historique des répartitions effectuées par la gérante.",
    placement: "right",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

const STEPS_ADMIN: Step[] = [
  DASHBOARD_WELCOME,
  SIDEBAR_STEP,
  {
    target: '[data-tour="nav-CONFIGURATION"]',
    title: "Configuration",
    content:
      "Gérez les utilisateurs, les rôles, l'identité société et les données de référence depuis ce module admin.",
    placement: "right",
  },
  NOTIFS_STEP,
  COMMON_FINAL,
];

export function getOnboardingSteps(role: UserRole): Step[] {
  let steps: Step[];
  switch (role) {
    case "gerante":
      steps = STEPS_GERANTE; break;
    case "pdg":
      steps = STEPS_PDG; break;
    case "gestionnaire":
      steps = STEPS_GESTIONNAIRE; break;
    case "controleur":
      steps = STEPS_CONTROLEUR; break;
    case "barman":
      steps = STEPS_BARMAN; break;
    case "cuisinier":
      steps = STEPS_CUISINIER; break;
    case "serveur":
      steps = STEPS_SERVEUR; break;
    case "chef_salle":
      steps = STEPS_CHEF_SALLE; break;
    case "admin":
    default:
      steps = STEPS_ADMIN;
  }
  return adaptForMobile(steps);
}

export const ONBOARDING_STORAGE_KEY = "mandjaro_onboarding_completed_v1";
